import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { CaretDown } from "components/icons/CaretDown";
import { YYYY_MM_DD } from "constants/date";
import { JESortColumnType } from "constants/journalEntrySources";
import dayjs from "dayjs";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { useLazyExportJournalEntriesQuery } from "store/apis/journalEntry";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { BackendError } from "types/utils/error";
import { openLink } from "utils/openLink";

export const ExportJournalEntry = () => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const query = useQuery();

  const [exportJournalEntries, { isFetching: isExporting }] =
    useLazyExportJournalEntriesQuery();
  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();

  const startDateRaw = query.get("START_DATE");
  const endDateRaw = query.get("END_DATE");

  const params = {
    pageNum: Number(query.get("page")) || 1,
    searchTerm: query.get("search_term") || null,
    startDate: startDateRaw ? dayjs(startDateRaw).format(YYYY_MM_DD) : null,
    endDate: endDateRaw ? dayjs(endDateRaw).format(YYYY_MM_DD) : null,
    sortCol: (query.get("sort_col") as JESortColumnType) || "DATE",
    sortOrder:
      query.get("sort_order") === "ASC" ? ("ASC" as const) : ("DSC" as const),
    ledger_types: query.get("LEDGER_TYPES") || null,
    accounting_method: query.get("ACCOUNTING_METHOD") || null,
    source: query.get("SOURCE") || null,
    recurring_entries_only: query.get("RECURRING_ENTRIES_ONLY")
      ? Boolean(query.get("RECURRING_ENTRIES_ONLY"))
      : null,
    hide_future_entries: query.get("HIDE_FUTURE_ENTRIES")
      ? Boolean(query.get("HIDE_FUTURE_ENTRIES"))
      : null,
    status: query.get("STATUS") || null,
  };

  const onExport = async (exportToMail: boolean) => {
    try {
      const { document_id } = await exportJournalEntries({
        groupId,
        entityId,
        ...params,
        export_to_mail: exportToMail,
      }).unwrap();

      if (exportToMail) {
        successToast({ message: "Export will be sent to your email" });
        return;
      }

      const { download_url } = await getPreviewUrl({
        groupId,
        fileId: document_id,
      }).unwrap();
      openLink(download_url);
      successToast({ message: "Export downloaded" });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild className="t-group">
        <div>
          <Button
            customType="secondary"
            size="xsmall"
            isLoading={isExporting}
            disabled={isExporting}
          >
            <div className="t-flex t-items-center t-gap-1.5">
              Export
              <div className="group-data-state-open:t-rotate-180">
                <CaretDown />
              </div>
            </div>
          </Button>
        </div>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content sideOffset={4} align="end" side="bottom">
          <ExportOptions
            onMailExport={() => onExport(true)}
            onLocalDownload={() => onExport(false)}
          />
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};
